import { type Params } from 'react-router';

import { convertToPIIString } from '@trello/privacy';
import { navigate } from '@trello/router/navigate';

import { getCurrentUsername } from './getCurrentUsername';

interface UsernameAuthGuardOptions {
  createRedirectUrl: (username: string) => string;
  getUsernameFromParams: (params: Params<string>) => string | undefined;
}

/**
 * Username Auth Middleware Factory
 *
 * This middleware validates that the username in the URL matches the currently logged-in user.
 * It prevents users from accessing other users' private pages by checking the `:username` parameter
 * against the authenticated user's actual username.
 *
 * If they don't match, it redirects to the correct user's page, or login page if no username is found.
 *
 * @param options Auth Middleware options
 * @returns React Router Middleware function
 */
export const createUsernameAuthGuard = (options: UsernameAuthGuardOptions) => {
  return async ({
    params,
  }: {
    params: Params<string>;
  }): Promise<Response | void> => {
    // If url contains a username parameter (i.e. :username, :name), validate logged in user matches
    const { createRedirectUrl, getUsernameFromParams } = options;

    const loginUrl = `/login?returnUrl=${encodeURIComponent(
      window.location.pathname + window.location.search,
    )}`;

    const usernameFromParams = getUsernameFromParams(params);

    if (!usernameFromParams) {
      return; // No username dynamic segment in URL to validate
    }

    const currentUsername = await getCurrentUsername();

    if (!currentUsername) {
      window.location.assign(loginUrl);

      // Return a 401 response to stop further processing of the route
      return new Response(null, {
        status: 401,
      });
    }

    const isNotSameUser =
      convertToPIIString(usernameFromParams) !== currentUsername;

    // Validate username dynamic segment in URL
    if (isNotSameUser) {
      const encodedUsername = encodeURIComponent(usernameFromParams);
      const resolvedRedirectUrl = createRedirectUrl(encodedUsername);

      navigate(resolvedRedirectUrl, { replace: true });

      // Return a 401 response to stop further processing of the route
      return new Response(null, {
        status: 401,
      });
    }
  };
};
